import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { PropertyRoles, UserProfile } from '@app/_models';
import { Property } from '@app/_models/propery.model';
import { AuthResponse } from '@app/_models/responses';
import { UserService, AuthenticationService } from '@app/_services';
import { DeviceService } from '@app/_services/device.service';
import { environment } from '@environments/environment';
import { faSearch, faCheckCircle, faBan, faSms, faEnvelope, faPlusCircle, faPencilAlt, faQuestionCircle, faMapMarkedAlt, faUser, faTimesCircle, faEyeSlash, faEye, faPaperPlane, faSpinner, faMapPin, faMapSigns, faSave } from '@fortawesome/free-solid-svg-icons';
import { ToastrService } from 'ngx-toastr';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-user-roles',
    templateUrl: './user-roles.component.html',
    styleUrls: ['./user-roles.component.css'],
    standalone: false
})
export class UserRolesComponent implements OnInit {

  faSearch = faSearch;
  faCheckCircle = faCheckCircle;
  faBan = faBan;
  faSms = faSms;
  faEnvelope = faEnvelope;
  faPlusCircle = faPlusCircle;
  faPencilAlt = faPencilAlt;
  faQuestionCircle = faQuestionCircle;
  faMapMarkedAlt = faMapMarkedAlt;
  faUser = faUser;
  faTimesCircle = faTimesCircle;
  faEyeSlash = faEyeSlash;
  faEye = faEye;
  faPaperPlane = faPaperPlane;
  faSpinner = faSpinner;
  faMapPin = faMapPin;
  faMapSigns = faMapSigns;
  faSave = faSave;

  showVerifyBtn = signal(false);
  showConfirmBtn = signal(false);

  help = signal(false);
  submitQuery = signal(false);
  submitAdd = signal(false);

  searchNumbers = signal('');
  sequenceNumbers: number[] = Array.from({length: 200}, (_, i) => i + 1);

  currentUser = signal<AuthResponse | null>(null);
  proproles = signal<PropertyRoles[]>([]);
  proplist = signal<Property[]>([]);
  searchRoles = signal('');
  userprofile = signal<UserProfile | null>(null);
  action = signal<string | null>(null);

  private destroyRef = inject(DestroyRef);

  constructor(private formBuilder: FormBuilder,
    private router: Router, private toastr: ToastrService,
    private route: ActivatedRoute, private deviceService: DeviceService,
    private userService: UserService, private authenticationService: AuthenticationService) { }

  addPropertyRole!: FormGroup;
  confirmPropertyRole!: FormGroup;

  ngOnInit(): void {
    this.addPropertyRole = this.formBuilder.group({
      username: ['', Validators.required],
      propertyid: ['', Validators.required],
      rolename: ['', Validators.required]
    });

    this.showVerifyBtn.set(true);

    this.confirmPropertyRole = this.formBuilder.group({
      username: ['', Validators.required],
      propertyid: ['', Validators.required],
      rolename: ['', Validators.required],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required]
    });

    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (params: Params) => this.action.set(params['action'] || null)
      });

    this.loadPropertyRoles();

    this.authenticationService.currentUser
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (user) => {
          this.currentUser.set(user || null);
          this.loadPropertiesByUser(user);
        }
      });
  }

  private loadPropertyRoles() {
    this.userService.getPropertyRoleApprovalList()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.proproles.set(data);
          if (!environment.production) {
            console.log('Property roles: ', data);
          }
        },
        error: () => {
          this.toastr.error('Fail to retrieve property roles for users', 'Error');
        }
      });
  }

  private loadPropertiesByUser(user: AuthResponse | null) {
    if (!user) {
      this.proplist.set([]);
      return;
    }

    if (user.role?.includes('ROLE_SUPER')) {
      this.userService.getAllPropertiesRoleAccessList()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (data) => {
            this.proplist.set(data);
          },
          error: () => {
            this.toastr.error('Fail to retrieve properties...', 'Error');
          }
        });
    } else {
      this.userService.getPropertiesByRoleAccessList(user.username!, 'ROLE_ADMIN')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (data) => {
            this.proplist.set(data);
          },
          error: () => {
            this.toastr.error('Fail to retrieve properties.', 'Error');
          }
        });
    }
  }

  get fa() { return this.addPropertyRole.controls; }
  get fe() { return this.confirmPropertyRole.controls; }
  get fc() { return this.addPropertyRole.controls; }

  addUserRole() {
    this.router.navigate(['/user-roles/add']);
  }

  queryUserRole() {
    this.router.navigate(['/user-roles/query']);
  }

  updateUserRole() {
    this.router.navigate(['/user-roles/update']);
  }

  onSubmitVerify() {
    this.submitQuery.set(true);

    if (this.addPropertyRole.invalid) {
      this.toastr.error("Please fill out form properly", "Error");
      return ;
    }

    let formdata = this.addPropertyRole.value;

    this.userService.getUserProfile(formdata.username)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          let fullnamearray = data.fullname!.split(" ");

          this.confirmPropertyRole.setValue({
            username: formdata.username,
            propertyid: formdata.propertyid,
            rolename: formdata.rolename,
            firstName: fullnamearray[0],
            lastName: fullnamearray[1]
          });

          this.router.navigate(['/user-roles/confirm']);
        },
        error: (error) => {
          this.toastr.error(error, "Error");
        }
      });
  }

  approveRole(propertyid: number) {
    if (!environment.production) {
      console.log('Approve Prop ID: ', propertyid);
    }

    const approverdata = {
      'approvedby': this.currentUser()?.username
    };

    this.userService.approveUserPropertyRole(propertyid, approverdata)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.toastr.success(data.message, 'Success');
          this.loadPropertyRoles();
        },
        error: (error) => {
          this.toastr.error(error, 'Error');
        }
      });
  }

  revokeRole(propertyid: number) {
    if (!environment.production) {
      console.log('Revoke Prop ID: ', propertyid);
    }

    this.userService.removeUserPropertyRole(propertyid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.toastr.success(data.message, 'Success');
          this.loadPropertyRoles();
        },
        error: (error) => {
          this.toastr.error(error, 'Error');
        }
      });
  }

  onSubmitConfirm() {
    this.submitAdd.set(true);

    if (this.confirmPropertyRole.invalid) {
      this.toastr.error("Please fill out form properly", "Error");
      return ;
    }

    let cformdata = this.confirmPropertyRole.value;

    this.userService.updateUserRole(cformdata)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.toastr.success('User Profile added successfully', 'Success');
        },
        error: (error) => {
          this.toastr.error(error, 'Error');
        }
      });
  }

}
